'use client';

import { Package, Tag, CheckCircle, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { createTile, type BaseTileRenderProps } from './BaseTile';
import type { ProductResult } from '../types';

function formatPrice(price?: number, currency?: string): string {
  if (price === undefined) return '';

  const currencyCode = currency || 'USD';

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format(price);
  } catch {
    return `${currencyCode} ${price.toFixed(2)}`;
  }
}

function StockBadge({ inStock }: { inStock?: boolean }) {
  if (inStock === undefined) return null;

  return (
    <Badge
      variant="outline"
      className={cn(
        'text-xs gap-1',
        inStock
          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
          : 'bg-red-500/10 text-red-400 border-red-500/30'
      )}
    >
      {inStock ? (
        <>
          <CheckCircle className="w-3 h-3" />
          In Stock
        </>
      ) : (
        <>
          <XCircle className="w-3 h-3" />
          Out of Stock
        </>
      )}
    </Badge>
  );
}

export const ProductTile = createTile<ProductResult>(
  'ProductTile',
  (result) => {
    const { metadata } = result;

    const renderProps: BaseTileRenderProps = {
      icon: Package,
      iconColor: 'text-emerald-400',
      title: result.title,
      subtitle: metadata.sku ? `SKU: ${metadata.sku}` : undefined,
      preview: result.preview,
      metadata: [],
      tags: [],
      rightContent: (
        <div className="flex items-center gap-2">
          {metadata.price !== undefined && (
            <span className="text-sm font-medium text-emerald-400">
              {formatPrice(metadata.price, metadata.currency)}
            </span>
          )}
          <StockBadge inStock={metadata.inStock} />
        </div>
      ),
    };

    if (metadata.category) {
      renderProps.tags = [metadata.category];
      renderProps.metadata!.push({
        label: 'Category',
        value: metadata.category,
        icon: Tag,
      });
    }

    if (metadata.imageUrl) {
      renderProps.bottomContent = (
        <div className="mt-2">
          <img
            src={metadata.imageUrl}
            alt={result.title}
            className="w-16 h-16 object-cover rounded-md border border-slate-700"
          />
        </div>
      );
    }

    return renderProps;
  }
);

export default ProductTile;
